'use strict'

import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { parseYmd, toYmd, getDayName, WORK_DAYS } from './scheduleUtils'

/**
 * Manages capacity matrix fetching and derived stats.
 *
 * @param {{ url: string, selectedScenarioId: string, focusDate: string, isFleetMode: boolean, scenarioMetrics: object }} config
 */
export default function useCapacityMatrix ({ url, selectedScenarioId, focusDate, isFleetMode, scenarioMetrics }) {
  const [capacityMatrix, setCapacityMatrix] = useState(null)
  const [capacityLoading, setCapacityLoading] = useState(false)

  const fetchCapacityMatrix = useCallback(async () => {
    if (!selectedScenarioId) return
    setCapacityLoading(true)
    try {
      const resp = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/best-fit`, {
        weekOf: focusDate || toYmd(new Date())
      })
      setCapacityMatrix(resp.data?.capacityMatrix || null)
    } catch (_err) {
      setCapacityMatrix(null)
    } finally {
      setCapacityLoading(false)
    }
  }, [url, selectedScenarioId, focusDate])

  useEffect(() => {
    if (selectedScenarioId) {
      fetchCapacityMatrix()
    } else {
      setCapacityMatrix(null)
    }
  }, [selectedScenarioId, focusDate, fetchCapacityMatrix])

  // Fleet-day aggregate stats for the focused date
  const fleetDayStats = useMemo(() => {
    if (!isFleetMode || !capacityMatrix?.routes?.length || !focusDate) return null
    const focusDayName = getDayName(parseYmd(focusDate))
    if (!focusDayName || !WORK_DAYS.includes(focusDayName)) return null

    let totalStops = 0, totalServiceMin = 0, totalDriveMin = 0, totalTotalMin = 0, totalOtMin = 0
    const crewDays = []

    capacityMatrix.routes.forEach(route => {
      const dm = route.days?.[focusDayName]
      if (!dm) return
      totalStops += dm.stops || 0
      totalServiceMin += dm.serviceMin || 0
      totalDriveMin += dm.driveMin || 0
      totalTotalMin += dm.totalMin || 0
      totalOtMin += dm.otMin || 0
      crewDays.push({ routeId: route.routeId, routeName: route.routeName, crewId: route.crewId, ...dm })
    })
    crewDays.sort((a, b) => (b.totalMin || 0) - (a.totalMin || 0))

    return {
      dayName: focusDayName, date: focusDate, crewCount: crewDays.length,
      totalStops, totalServiceMin, totalDriveMin, totalTotalMin, totalOtMin,
      crewsInOt: crewDays.filter(c => (c.otMin || 0) > 0).length,
      crewDays
    }
  }, [isFleetMode, capacityMatrix, focusDate])

  const fleetAvgWeeklyMinutes = useMemo(
    () => Number(scenarioMetrics?.fleet?.avgCrewLoad || 0),
    [scenarioMetrics]
  )

  // Lookup map: routeId or crewId -> day stats for focusDate
  const capacityByRoute = useMemo(() => {
    const map = new Map()
    if (!capacityMatrix?.routes?.length || !focusDate) return map
    const dayName = getDayName(parseYmd(focusDate))
    if (!dayName || !WORK_DAYS.includes(dayName)) return map
    capacityMatrix.routes.forEach(route => {
      const dm = route.days?.[dayName]
      if (!dm) return
      const entry = { ...dm, routeName: route.routeName, routeId: route.routeId, crewId: route.crewId }
      if (route.routeId) map.set(String(route.routeId), entry)
      if (route.crewId) map.set(String(route.crewId), entry)
    })
    return map
  }, [capacityMatrix, focusDate])

  return {
    capacityMatrix,
    setCapacityMatrix,
    capacityLoading,
    fetchCapacityMatrix,
    fleetDayStats,
    fleetAvgWeeklyMinutes,
    capacityByRoute
  }
}
